import {
  Badge,
  Skeleton,
  Text,
  Box,
  Textarea,
  VStack,
  HStack,
  Icon,
  Grid,
  GridItem,
  Separator,
  IconButton,
  Table,
  NativeSelect,
} from "@chakra-ui/react";
import { ChangeEvent, Dispatch, useEffect, useState } from "react";
import {
  Student,
  Subject,
  BehavioralSubject,
  GradeCriteria,
  CognitiveResultEntry,
  BehavioralResultEntry,
  SummaryResultEntry,
  AssessmentType,
} from "../../../services/Dtos";
import { useSchoolsContext } from "../../../Contexts/schoolContext";
import Empty from "../../CustomUi/Empty";
import { useNavigate } from "react-router-dom";
import { getColorForGrade } from "../../../services/Utils";
import { useRoleAccess } from "../../../hooks/useRoleAccess";
import { Role } from "../../../services/Enums";
import { useStyles } from "../../../hooks/useStyles";
import useToaster from "../../../hooks/useToaster";

import useThemeColors from "../../../hooks/useThemeColors";
import Cad from "../../CustomUi/Cad";
import CustomButton from "../../CustomUi/CustomButton";
import CustomNumberInput from "../../CustomUi/CustomNumberInput";
import useIcons from "../../../hooks/useIcons";
import { Tooltip } from "../../ui/tooltip";
import { useBreakPoint } from "../../../hooks/useBreakPoint";

interface Props {
  chosenStudent: Student | null;
  selectedClassId: string | null;
  setUnsavedChanges: Dispatch<React.SetStateAction<boolean>>;
  cognitiveResultEntries: CognitiveResultEntry[];
  behavioralResultEntries: BehavioralResultEntry[];
  summaryResultEntry: SummaryResultEntry;
  editedCognitiveEntries: CognitiveResultEntry[];
  editedBehavioralEntries: BehavioralResultEntry[];
  editedSummaryEntry: SummaryResultEntry;
  setEditedCognitiveEntries: Dispatch<
    React.SetStateAction<CognitiveResultEntry[]>
  >;
  setEditedBehavioralEntries: Dispatch<
    React.SetStateAction<BehavioralResultEntry[]>
  >;
  setEditedSummaryEntry: Dispatch<React.SetStateAction<SummaryResultEntry>>;
  setSummaryResultEntry: Dispatch<React.SetStateAction<SummaryResultEntry>>;
  currentClassSubjects: Subject[];
  currentBehavioralSubjects: BehavioralSubject[];
  editingSubjectIds: string[];
  setEditingSubjectIds: Dispatch<React.SetStateAction<string[]>>;
  fetchingClassResult: boolean;
  currentLevel: string;
  filteredAssessmentTypes: AssessmentType[];
  filteredGradeCriterias: GradeCriteria[];
}

export default function ResultEntryTable({
  chosenStudent,
  selectedClassId,
  setUnsavedChanges,
  cognitiveResultEntries,
  behavioralResultEntries,
  summaryResultEntry,
  editedCognitiveEntries,
  editedBehavioralEntries,
  setEditedCognitiveEntries,
  setEditedBehavioralEntries,
  currentClassSubjects,
  currentBehavioralSubjects,
  editingSubjectIds,
  setEditingSubjectIds,
  editedSummaryEntry,
  setEditedSummaryEntry,
  fetchingClassResult,
  filteredAssessmentTypes,
  filteredGradeCriterias,
}: Props) {
  const { school, assessmentTypes, levels, classes } = useSchoolsContext();
  const { users } = useSchoolsContext();
  const colors = useThemeColors();
  const { scrollBar } = useStyles();
  const { showErrorToast } = useToaster();
  const { isMobile } = useBreakPoint();
  const { hasRoleOrHigher } = useRoleAccess();
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});
  const [showMobileView, setShowMobileView] = useState(false);
  const icons = useIcons();

  // Check if current term is a leap term
  const isLeapTerm = school?.currentTermId === school?.leapTermId;

  const navigate = useNavigate();

  const initialResultEntryState: CognitiveResultEntry = {
    id: "",
    studentId: chosenStudent?.id || "",
    subjectId: "",
    termId: school?.currentTermId || "",
    sessionId: school?.currentSessionId || "",
    classId: selectedClassId || "",
    assessmentValues: {},
    totalScore: null,
    grade: "",
    classAverage: null,
    classPosition: null,
  };

  const initialBehavioralResultEntryState: BehavioralResultEntry = {
    id: "",
    studentId: chosenStudent?.id || "",
    behavioralSubjectId: "",
    termId: school?.currentTermId || "",
    sessionId: school?.currentSessionId || "",
    classId: selectedClassId || "",
    grade: "",
  };

  const initialSummaryResultEntryState: SummaryResultEntry = {
    id: "",
    publicResultId: "",
    studentId: chosenStudent?.id || "",
    subjectId: "",
    termId: school?.currentTermId || "",
    sessionId: school?.currentSessionId || "",
    classId: selectedClassId || "",
    daysPresent: 0,
    daysPunctual: 0,
    averageScore: 0,
    maxMarks: 0,
    positionInClass: 0,
    headRemark: "",
    formTeacherRemark: "",
    grade: "",
  };

  useEffect(() => {
    if (summaryResultEntry) {
      setEditedSummaryEntry(summaryResultEntry);
    } else {
      setEditedSummaryEntry(initialSummaryResultEntryState);
    }
  }, [summaryResultEntry, chosenStudent, selectedClassId]);

  // Auto-switch to mobile view on small screens
  useEffect(() => {
    setShowMobileView(isMobile || false);
  }, [isMobile]);

  const getMaxScore = (typeName: string): number => {
    const assessmentType = assessmentTypes.find(
      (type) => type.name === typeName
    );
    return assessmentType ? assessmentType.maxScore : Infinity;
  };

  const validateInput = (
    value: number,
    maxScore: number,
    fieldName: string
  ): string | null => {
    if (value < 0) return `${fieldName} cannot be negative`;
    if (value > maxScore) return `${fieldName} cannot exceed ${maxScore}`;
    if (value % 1 !== 0) return `${fieldName} must be a whole number`;
    return null;
  };

  const handleCognitiveInputChange = (
    subjectId: string,
    field: string,
    value: string
  ) => {
    setUnsavedChanges(true);
    const maxScore = getMaxScore(field);
    const numValue = Number(value);

    // Clear validation error for this field
    const errorKey = `${subjectId}-${field}`;
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[errorKey];
      return newErrors;
    });

    // Validate input
    const error = validateInput(numValue, maxScore, field);
    if (error) {
      setValidationErrors((prev) => ({
        ...prev,
        [errorKey]: error,
      }));
      if (numValue > maxScore) {
        window.alert(`${field} cannot exceed ${maxScore}`);
      } else {
        showErrorToast(error);
        return;
      }
    }

    setEditedCognitiveEntries((prev) => {
      const existingEntry = prev.find((entry) => entry.subjectId === subjectId);
      if (existingEntry) {
        const assessments = existingEntry.assessmentValues
          ? existingEntry.assessmentValues
          : {};
        assessments[field] = numValue;
        return prev.map((entry) =>
          entry.subjectId === subjectId
            ? { ...entry, assessmentValues: assessments }
            : entry
        );
      } else {
        const originalEntry =
          cognitiveResultEntries.find(
            (entry) => entry.subjectId === subjectId
          ) || initialResultEntryState;
        const assessments = originalEntry.assessmentValues
          ? originalEntry.assessmentValues
          : {};
        assessments[field] = numValue;
        return [
          ...prev,
          {
            ...originalEntry,
            subjectId,
            assessmentValues: assessments,
          },
        ];
      }
    });

    if (!editingSubjectIds.includes(subjectId)) {
      setEditingSubjectIds((prev) => [...prev, subjectId]);
    }
  };

  const handleBehavioralGradeChange = (
    behavioralSubjectId: string,
    grade: string
  ) => {
    setUnsavedChanges(true);

    setEditedBehavioralEntries((prev) => {
      const existingEntry = prev.find(
        (entry) => entry.behavioralSubjectId === behavioralSubjectId
      );
      if (existingEntry) {
        return prev.map((entry) =>
          entry.behavioralSubjectId === behavioralSubjectId
            ? { ...entry, grade }
            : entry
        );
      } else {
        const originalEntry =
          behavioralResultEntries.find(
            (entry) => entry.behavioralSubjectId === behavioralSubjectId
          ) || initialBehavioralResultEntryState;
        return [
          ...prev,
          {
            ...originalEntry,
            behavioralSubjectId,
            grade,
          },
        ];
      }
    });

    if (!editingSubjectIds.includes(behavioralSubjectId)) {
      setEditingSubjectIds((prev) => [...prev, behavioralSubjectId]);
    }
  };

  const handleSummaryInputChange = (
    event: ChangeEvent<
      HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = event.target;
    setUnsavedChanges(true);

    // Validate numeric inputs
    if (name === "daysPresent" || name === "daysPunctual") {
      const numValue = parseInt(value, 10);
      if (numValue < 0) {
        showErrorToast(
          `${
            name === "daysPresent" ? "Days Present" : "Days Punctual"
          } cannot be negative`
        );
        return;
      }
    }

    setEditedSummaryEntry((prev) => ({
      ...prev,
      [name]:
        name === "daysPresent" || name === "daysPunctual"
          ? parseInt(value, 10)
          : value,
    }));
  };

  const mergeCognitiveEntries = (subjectId: string): CognitiveResultEntry => {
    const entryFromResults =
      cognitiveResultEntries.find((e) => e.subjectId === subjectId) ||
      initialResultEntryState;
    const entryFromEdited =
      editedCognitiveEntries.find((e) => e.subjectId === subjectId) ||
      ({} as CognitiveResultEntry);

    const mergedEntry: CognitiveResultEntry = {
      ...entryFromResults,
      ...entryFromEdited,
      subjectId,
      assessmentValues: {
        ...entryFromResults.assessmentValues,
        ...entryFromEdited.assessmentValues,
      },
    };

    return mergedEntry;
  };

  const mergeBehavioralEntries = (
    behavioralSubjectId: string
  ): BehavioralResultEntry => {
    const entryFromResults =
      behavioralResultEntries.find(
        (e) => e.behavioralSubjectId === behavioralSubjectId
      ) || initialBehavioralResultEntryState;
    const entryFromEdited =
      editedBehavioralEntries.find(
        (e) => e.behavioralSubjectId === behavioralSubjectId
      ) || ({} as BehavioralResultEntry);

    const mergedEntry: BehavioralResultEntry = {
      ...entryFromResults,
      ...entryFromEdited,
      behavioralSubjectId,
      grade: entryFromEdited.grade || entryFromResults.grade,
    };

    return mergedEntry;
  };

  const getFormTeacherName = () => {
    const teacherId = classes.find(
      (c) => c.id === selectedClassId
    )?.formTeacherId;
    const teacher = users.find((u) => u.id === teacherId);
    return `${teacher?.firstName || ""} ${teacher?.lastName || ""}`;
  };

  const getHeadTeacherName = () => {
    const cls = classes.find((c) => c.id === selectedClassId);
    const teacherId = levels.find((l) => l.id === cls?.levelId)?.adminId;
    const teacher = users.find((u) => u.id === teacherId);
    return `${teacher?.firstName || ""} ${teacher?.lastName || ""}`;
  };

  const getSubjectProgress = (subjectId: string) => {
    const entry = mergeCognitiveEntries(subjectId);
    const assessments = entry.assessmentValues || {};
    const filledFields = Object.keys(assessments).filter((key) => {
      const value = assessments[key];
      return value !== null && value !== undefined;
    }).length;
    return (filledFields / filteredAssessmentTypes.length) * 100;
  };

  const getBehavioralSubjectProgress = (behavioralSubjectId: string) => {
    const entry = mergeBehavioralEntries(behavioralSubjectId);
    return entry.grade && entry.grade.trim() !== "" ? 100 : 0;
  };

  const getOverallProgress = () => {
    const totalSubjects =
      currentClassSubjects.length + currentBehavioralSubjects.length;
    if (totalSubjects === 0) return 0;

    const cognitiveProgress = currentClassSubjects.reduce((sum, subject) => {
      return sum + getSubjectProgress(subject.id);
    }, 0);

    const behavioralProgress = currentBehavioralSubjects.reduce(
      (sum, subject) => {
        return sum + getBehavioralSubjectProgress(subject.id);
      },
      0
    );

    const totalProgress = cognitiveProgress + behavioralProgress;
    return totalProgress / totalSubjects;
  };

  const getProgressColor = (progress: number) => {
    if (progress === 0) return "gray";
    if (progress < 25) return "red";
    if (progress < 50) return "orange";
    if (progress < 75) return "yellow";
    if (progress < 100) return "blue";
    return "green";
  };

  const gradeOptions = filteredGradeCriterias.map((grade) => ({
    label: grade.grade,
    value: grade.grade,
  }));

  return (
    <>
      {!school?.currentSessionId || !school.currentTermId ? (
        <Empty
          message="Current Session or Term not set"
          hasButton
          handleClick={() => navigate("/settings/term")}
          hidePicture
          buttonText="Go to Settings"
        />
      ) : (
        <>
          <VStack gap={6} align="stretch" w="full">
            {/* Progress Indicator */}
            {chosenStudent && (
              <Cad px={"10px"} py={"10px"} bg={colors.background.primary}>
                <VStack gap={3}>
                  <HStack
                    justify="space-between"
                    w="full"
                    align="center"
                    flexDir={isMobile ? "column" : "row"}
                  >
                    <HStack gap={3} align="center">
                      <Text fontWeight="semibold" fontSize="sm">
                        Entry Progress
                      </Text>
                      <Badge
                        colorPalette={getProgressColor(getOverallProgress())}
                        variant="subtle"
                        fontSize="xs"
                        px={2}
                        py={1}
                      >
                        {Math.round(getOverallProgress())}% Complete
                      </Badge>
                    </HStack>
                    <HStack gap={4} fontSize="xs">
                      <HStack gap={1}>
                        <Text>Cognitive:</Text>
                        <Text fontWeight="semibold">
                          {
                            currentClassSubjects.filter(
                              (s) => getSubjectProgress(s.id) === 100
                            ).length
                          }
                          /{currentClassSubjects.length}
                        </Text>
                      </HStack>
                      <HStack gap={1}>
                        <Text>Behavioral:</Text>
                        <Text fontWeight="semibold">
                          {
                            currentBehavioralSubjects.filter(
                              (s) => getBehavioralSubjectProgress(s.id) === 100
                            ).length
                          }
                          /{currentBehavioralSubjects.length}
                        </Text>
                      </HStack>
                      <HStack gap={1}>
                        <Text>Assessments:</Text>
                        <Text fontWeight="semibold">
                          {filteredAssessmentTypes.length}
                        </Text>
                      </HStack>
                    </HStack>
                  </HStack>
                  <Box
                    w="full"
                    h="8px"
                    bg={colors.background.tertiary}
                    borderRadius="lg"
                    overflow="hidden"
                  >
                    <Box
                      h="full"
                      bg={`${getProgressColor(getOverallProgress())}.400`}
                      w={`${getOverallProgress()}%`}
                      transition="width 0.3s ease"
                    />
                  </Box>
                </VStack>
              </Cad>
            )}

            {/* View Toggle for Mobile */}
            {isMobile && (
              <HStack justify="center" gap={4}>
                <CustomButton
                  size="sm"
                  variant={showMobileView ? "solid" : "outline"}
                  icon={icons.utility.mobile}
                  label="Mobile View"
                  onClick={() => setShowMobileView(true)}
                />
                <CustomButton
                  size="sm"
                  variant={!showMobileView ? "solid" : "outline"}
                  icon={icons.utility.desktop}
                  label="Desktop View"
                  onClick={() => setShowMobileView(false)}
                />
              </HStack>
            )}

            {/* Cognitive Analysis Section */}
            <VStack gap={4} align="stretch">
              <Box
                w={"100%"}
                borderWidth="1px"
                borderX={"none"}
                p={3}
                borderRadius="md"
                borderLeft={"4px solid"}
                borderLeftColor={colors.border.primary}
              >
                <HStack justify="space-between">
                  <HStack gap={2} align="center">
                    <Text fontSize="md" fontWeight="semibold">
                      Cognitive Analysis of Results
                    </Text>
                  </HStack>
                  {hasRoleOrHigher(Role.Editor) && (
                    <HStack gap={2}>
                      <Tooltip
                        content="Use Tab/Enter to navigate, Arrow keys to move between subjects"
                        positioning={{ placement: "top" }}
                        openDelay={100}
                      >
                        <IconButton
                          variant={"ghost"}
                          size={"xs"}
                          colorPalette={"gray"}
                          _hover={{ colorPalette: "gray" }}
                        >
                          <Icon
                            as={icons.utility.keyboard}
                            boxSize={4}
                            cursor={"help"}
                          />
                        </IconButton>
                      </Tooltip>
                    </HStack>
                  )}
                </HStack>
              </Box>

              {currentClassSubjects.length ? (
                filteredAssessmentTypes.length ? (
                  !showMobileView ? (
                    <Box css={scrollBar}>
                      <Table.Root borderRadius={5} w="full">
                        <Table.Header position="sticky" top={-5} zIndex={2}>
                          <Table.Row bg={colors.background.tertiary}>
                            <Table.ColumnHeader
                              whiteSpace="nowrap"
                              minW="150px"
                              p={2}
                              textAlign="left"
                              borderBottom="1px solid"
                              borderColor={colors.border.secondary}
                            >
                              Subject
                            </Table.ColumnHeader>
                            {filteredAssessmentTypes.map((type) => (
                              <Table.ColumnHeader
                                w="80px"
                                textAlign="center"
                                key={type.id}
                                p={2}
                                borderBottom="1px solid"
                                borderColor={colors.border.secondary}
                              >
                                <VStack gap={0}>
                                  <Text fontSize="xs" fontWeight="bold">
                                    {type.shortName}
                                  </Text>
                                  <Text fontSize="xs" color="gray.500">
                                    Max: {type.maxScore}
                                  </Text>
                                </VStack>
                              </Table.ColumnHeader>
                            ))}
                            <Table.ColumnHeader
                              textAlign="center"
                              minW="80px"
                              p={2}
                              borderBottom="1px solid"
                              borderColor={colors.border.secondary}
                              fontSize="xs"
                              fontWeight="bold"
                            >
                              TOTAL
                            </Table.ColumnHeader>
                            <Table.ColumnHeader
                              textAlign="center"
                              minW="80px"
                              p={2}
                              borderBottom="1px solid"
                              borderColor={colors.border.secondary}
                              fontSize="xs"
                              fontWeight="bold"
                            >
                              AVG
                            </Table.ColumnHeader>
                            <Table.ColumnHeader
                              textAlign="center"
                              minW="80px"
                              p={2}
                              borderBottom="1px solid"
                              borderColor={colors.border.secondary}
                              fontSize="xs"
                              fontWeight="bold"
                            >
                              POS
                            </Table.ColumnHeader>
                            <Table.ColumnHeader
                              textAlign="center"
                              minW="80px"
                              p={2}
                              borderBottom="1px solid"
                              borderColor={colors.border.secondary}
                              fontSize="xs"
                              fontWeight="bold"
                            >
                              GRADE
                            </Table.ColumnHeader>
                          </Table.Row>
                        </Table.Header>

                        <Table.Body>
                          {currentClassSubjects.map((subject, index) => {
                            const entry = mergeCognitiveEntries(subject.id);
                            const assessments = entry.assessmentValues || {};
                            const progress = getSubjectProgress(subject.id);

                            return (
                              <Table.Row
                                key={subject.id}
                                bg={
                                  index % 2 === 0
                                    ? colors.background.primary
                                    : colors.background.secondary
                                }
                              >
                                <Table.Cell p={2} whiteSpace="nowrap">
                                  <VStack gap={1} align="start">
                                    <Text fontWeight="medium" fontSize="sm">
                                      {subject.subjectName}
                                    </Text>
                                    <Box
                                      w="100px"
                                      h="4px"
                                      bg={colors.background.tertiary}
                                      borderRadius="full"
                                      overflow="hidden"
                                    >
                                      <Box
                                        h="full"
                                        bg={`${getProgressColor(progress)}.400`}
                                        w={`${progress}%`}
                                        transition="width 0.3s ease"
                                      />
                                    </Box>
                                  </VStack>
                                </Table.Cell>
                                {filteredAssessmentTypes.map((type) =>
                                  chosenStudent ? (
                                    fetchingClassResult ? (
                                      <Table.Cell key={type.id} p={2}>
                                        <Skeleton
                                          m="auto"
                                          height="20px"
                                          w="30px"
                                        />
                                      </Table.Cell>
                                    ) : (
                                      <Table.Cell
                                        key={type.id}
                                        py={1}
                                        px={1}
                                        w="80px"
                                      >
                                        <VStack gap={0}>
                                          {hasRoleOrHigher(Role.Editor) ? (
                                            <CustomNumberInput
                                              minW={"70px"}
                                              value={
                                                assessments[
                                                  type.name
                                                ]?.toString() || ""
                                              }
                                              onChange={(value) =>
                                                handleCognitiveInputChange(
                                                  subject.id,
                                                  type.name,
                                                  value
                                                )
                                              }
                                              min={0}
                                              max={type.maxScore}
                                              error={
                                                validationErrors[
                                                  `${subject.id}-${type.name}`
                                                ]
                                              }
                                            />
                                          ) : (
                                            <Text
                                              fontSize="sm"
                                              textAlign="center"
                                            >
                                              {assessments[type.name] || "-"}
                                            </Text>
                                          )}
                                        </VStack>
                                      </Table.Cell>
                                    )
                                  ) : (
                                    <Table.Cell key={type.id}></Table.Cell>
                                  )
                                )}
                                <Table.Cell textAlign="center" p={2}>
                                  {editingSubjectIds.includes(subject.id) ||
                                  fetchingClassResult ? (
                                    <Skeleton m="auto" height="20px" w="30px" />
                                  ) : (
                                    <Text fontWeight="semibold">
                                      {entry.totalScore}
                                    </Text>
                                  )}
                                </Table.Cell>
                                <Table.Cell textAlign="center" p={2}>
                                  {editingSubjectIds.includes(subject.id) ||
                                  fetchingClassResult ? (
                                    <Skeleton m="auto" height="20px" w="30px" />
                                  ) : (
                                    <Text fontSize="sm">
                                      {entry.classAverage}
                                    </Text>
                                  )}
                                </Table.Cell>
                                <Table.Cell textAlign="center" p={2}>
                                  {editingSubjectIds.includes(subject.id) ||
                                  fetchingClassResult ? (
                                    <Skeleton m="auto" height="20px" w="30px" />
                                  ) : (
                                    <Text fontSize="sm">
                                      {entry.classPosition}
                                    </Text>
                                  )}
                                </Table.Cell>
                                <Table.Cell textAlign="center" p={2}>
                                  {editingSubjectIds.includes(subject.id) ||
                                  fetchingClassResult ? (
                                    <Skeleton m="auto" height="20px" w="30px" />
                                  ) : (
                                    entry.grade && (
                                      <Badge
                                        variant="solid"
                                        fontSize="sm"
                                        colorPalette={getColorForGrade(
                                          filteredGradeCriterias,
                                          entry.grade
                                        )}
                                      >
                                        {entry.grade}
                                      </Badge>
                                    )
                                  )}
                                </Table.Cell>
                              </Table.Row>
                            );
                          })}
                        </Table.Body>
                      </Table.Root>
                    </Box>
                  ) : (
                    <VStack gap={4} align="stretch">
                      {currentClassSubjects.map((subject) => {
                        const entry = mergeCognitiveEntries(subject.id);
                        const assessments = entry.assessmentValues || {};
                        const progress = getSubjectProgress(subject.id);

                        return (
                          <Cad key={subject.id}>
                            <VStack gap={3} align="stretch">
                              <HStack justify="space-between">
                                <Text fontWeight="semibold">
                                  {subject.subjectName}
                                </Text>
                                <Badge
                                  variant="outline"
                                  colorPalette={getColorForGrade(
                                    filteredGradeCriterias,
                                    entry.grade
                                  )}
                                >
                                  {entry.grade}
                                </Badge>
                              </HStack>

                              <Box
                                w="full"
                                h="4px"
                                bg={colors.background.tertiary}
                                borderRadius="full"
                                overflow="hidden"
                              >
                                <Box
                                  h="full"
                                  bg={`${getProgressColor(progress)}.400`}
                                  w={`${progress}%`}
                                  transition="width 0.3s ease"
                                />
                              </Box>

                              <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                                {filteredAssessmentTypes.map((type) => (
                                  <GridItem key={type.id}>
                                    <VStack gap={1} align="stretch">
                                      <Text fontSize="xs" fontWeight="medium">
                                        {type.shortName} (Max: {type.maxScore})
                                      </Text>
                                      {hasRoleOrHigher(Role.Editor) ? (
                                        <CustomNumberInput
                                          value={
                                            assessments[
                                              type.name
                                            ]?.toString() || ""
                                          }
                                          onChange={(value) =>
                                            handleCognitiveInputChange(
                                              subject.id,
                                              type.name,
                                              value
                                            )
                                          }
                                          min={0}
                                          max={type.maxScore}
                                        />
                                      ) : (
                                        <Text fontSize="sm" textAlign="center">
                                          {assessments[type.name] || "-"}
                                        </Text>
                                      )}
                                    </VStack>
                                  </GridItem>
                                ))}
                              </Grid>

                              <HStack justify="space-between" pt={2}>
                                <Text fontSize="sm" fontWeight="semibold">
                                  Total: {entry.totalScore}
                                </Text>
                                <Text fontSize="sm">
                                  Position: {entry.classPosition}
                                </Text>
                              </HStack>
                            </VStack>
                          </Cad>
                        );
                      })}
                    </VStack>
                  )
                ) : (
                  <Empty
                    message="Assessment Types not set"
                    hidePicture
                    hasButton
                    buttonText="Go to Settings"
                    handleClick={() => navigate("/settings/assessment")}
                  />
                )
              ) : (
                <Empty
                  message="No cognitive subjects assigned to this class"
                  hidePicture
                  hasButton
                  buttonText="Go to Class/Subject Settings"
                  handleClick={() => navigate("/admin/academic/classes")}
                />
              )}
            </VStack>
            <Separator
              size={"lg"}
              variant={"dotted"}
              my={5}
              borderColor={colors.border.secondary}
            />

            {/* Behavioral Analysis Section */}
            <VStack gap={4} align="stretch">
              <Box
                w={"100%"}
                borderWidth="1px"
                borderX={"none"}
                p={3}
                borderRadius="md"
                borderLeft={"4px solid"}
                borderLeftColor={colors.border.primary}
              >
                <HStack justify="space-between" align="center">
                  <HStack gap={2} align="center">
                    <Text fontSize="md" fontWeight="semibold">
                      Behavioral Analysis of Results
                    </Text>
                    {isLeapTerm && (
                      <Box
                        title="The current term is set as the Leap Term and its behavioral grades will be used for cumulative calculations"
                        cursor="help"
                      >
                        <Badge
                          colorPalette={colors.schoolAccent.primary}
                          variant="subtle"
                          px={2}
                          py={1}
                          fontSize="xs"
                        >
                          <Icon as={icons.utility.infoCircle} mr={1} />
                          Cumulative
                        </Badge>
                      </Box>
                    )}
                  </HStack>
                </HStack>
              </Box>

              {currentBehavioralSubjects.length ? (
                <Grid
                  templateColumns={
                    isMobile ? "1fr" : "repeat(auto-fit, minmax(250px, 1fr))"
                  }
                  gap={4}
                  bg={colors.background.primary}
                >
                  {currentBehavioralSubjects.map((subject) => {
                    const entry = mergeBehavioralEntries(subject.id);
                    const grades = entry.grade || "";
                    return (
                      <Box
                        key={subject.id}
                        bg={colors.background.primary}
                        p={2}
                        borderRadius="md"
                      >
                        <HStack justify="space-between" align="center">
                          <HStack gap={3} align="center">
                            <Box
                              flexShrink={0}
                              w="12px"
                              h="20px"
                              borderRadius="lg"
                              bg={
                                getColorForGrade(
                                  filteredGradeCriterias,
                                  entry.grade
                                ) + ".400"
                              }
                            />
                            <VStack gap={0} align="start">
                              <Text fontWeight="medium">{subject.name}</Text>
                            </VStack>
                          </HStack>
                          {chosenStudent ? (
                            fetchingClassResult ? (
                              <Skeleton height="32px" w="100px" />
                            ) : (
                              <NativeSelect.Root
                                disabled={!hasRoleOrHigher(Role.Editor)}
                              >
                                <NativeSelect.Field
                                  placeholder="Select Grade"
                                  value={grades}
                                  onChange={(e) =>
                                    handleBehavioralGradeChange(
                                      subject.id,
                                      e.target.value
                                    )
                                  }
                                >
                                  {gradeOptions.map((option) => (
                                    <option
                                      value={option.value}
                                      key={option.value}
                                    >
                                      {option.label}
                                    </option>
                                  ))}
                                </NativeSelect.Field>
                                <NativeSelect.Indicator />
                              </NativeSelect.Root>
                            )
                          ) : (
                            <Text color="gray.400">No student selected</Text>
                          )}
                        </HStack>
                      </Box>
                    );
                  })}
                </Grid>
              ) : (
                <Empty
                  message="No Behavioral subjects assigned to this class"
                  hidePicture
                  hasButton
                  buttonText="Go to Class/Subject Settings"
                  handleClick={() => navigate("/admin/academic/classes")}
                />
              )}
            </VStack>

            <Separator
              size={"lg"}
              variant={"dotted"}
              my={5}
              borderColor={colors.border.secondary}
            />

            {/* Summary Section */}
            <VStack gap={4} align="stretch">
              <Box
                w={"100%"}
                borderWidth="1px"
                borderX={"none"}
                p={3}
                borderRadius="md"
                borderLeft={"4px solid"}
                borderLeftColor={colors.border.primary}
              >
                <HStack justify="space-between" align="center">
                  <HStack gap={2} align="center">
                    <Text fontSize="md" fontWeight="semibold">
                      Summary of Results
                    </Text>
                    {isLeapTerm && (
                      <Box
                        title="The current term is set as the Leap Term and its summary data will be used for cumulative calculations"
                        cursor="help"
                      >
                        <Badge
                          colorPalette={colors.schoolAccent.primary}
                          variant="subtle"
                          px={2}
                          py={1}
                          fontSize="xs"
                        >
                          <Icon as={icons.utility.infoCircle} mr={1} />
                          Cumulative
                        </Badge>
                      </Box>
                    )}
                  </HStack>
                </HStack>
              </Box>

              <Grid
                templateColumns={
                  isMobile ? "1fr" : "repeat(auto-fit, minmax(200px, 1fr))"
                }
                gap={4}
              >
                <Box bg={colors.background.primary} p={2} borderRadius="md">
                  <HStack gap={3} align="stretch">
                    <Text fontWeight="medium">Days Present</Text>
                    {chosenStudent ? (
                      fetchingClassResult ? (
                        <Skeleton height="32px" />
                      ) : hasRoleOrHigher(Role.Editor) ? (
                        <CustomNumberInput
                          value={
                            editedSummaryEntry.daysPresent?.toString() || ""
                          }
                          onChange={(value) => {
                            setEditedSummaryEntry((prev) => ({
                              ...prev,
                              daysPresent: Number(value),
                            }));
                            setUnsavedChanges(true);
                          }}
                          min={0}
                          w="full"
                        />
                      ) : (
                        <Text fontSize="sm">
                          {editedSummaryEntry.daysPresent || "-"}
                        </Text>
                      )
                    ) : (
                      <Text color="gray.400">No student selected</Text>
                    )}
                  </HStack>
                </Box>

                <Box bg={colors.background.primary} p={2} borderRadius="md">
                  <HStack gap={3} align="stretch">
                    <Text fontWeight="medium">Days Punctual</Text>
                    {chosenStudent ? (
                      fetchingClassResult ? (
                        <Skeleton height="32px" />
                      ) : hasRoleOrHigher(Role.Editor) ? (
                        <CustomNumberInput
                          w="full"
                          value={
                            editedSummaryEntry.daysPunctual?.toString() || ""
                          }
                          onChange={(value) => {
                            setEditedSummaryEntry((prev) => ({
                              ...prev,
                              daysPunctual: Number(value),
                            }));
                            setUnsavedChanges(true);
                          }}
                          min={0}
                        />
                      ) : (
                        <Text fontSize="sm">
                          {editedSummaryEntry.daysPunctual || "-"}
                        </Text>
                      )
                    ) : (
                      <Text color="gray.400">No student selected</Text>
                    )}
                  </HStack>
                </Box>
              </Grid>

              <Box bg={colors.background.primary} p={2} borderRadius="md">
                <VStack gap={3} align="stretch">
                  <HStack justify="space-between">
                    <Text fontWeight="medium">Form Teacher's Remark</Text>
                    <Text fontSize="xs" color="gray.500">
                      {getFormTeacherName()}
                    </Text>
                  </HStack>
                  {chosenStudent ? (
                    fetchingClassResult ? (
                      <Skeleton height="80px" />
                    ) : hasRoleOrHigher(Role.Editor) ? (
                      <Textarea
                        name="formTeacherRemark"
                        bg={colors.background.secondary}
                        value={editedSummaryEntry.formTeacherRemark || ""}
                        onChange={handleSummaryInputChange}
                        placeholder="Enter form teacher's remarks..."
                        minH="80px"
                      />
                    ) : (
                      <Text
                        fontSize="sm"
                        minH="80px"
                        p={3}
                        bg={colors.background.secondary}
                        borderRadius="md"
                      >
                        {editedSummaryEntry.formTeacherRemark || "No remarks"}
                      </Text>
                    )
                  ) : (
                    <Text color="gray.400">No student selected</Text>
                  )}
                </VStack>
              </Box>

              <Box bg={colors.background.primary} p={2} borderRadius="md">
                <VStack gap={3} align="stretch">
                  <HStack justify="space-between">
                    <Text fontWeight="medium">Head or Principal's Remark</Text>
                    <Text fontSize="xs" color="gray.500">
                      {getHeadTeacherName()}
                    </Text>
                  </HStack>
                  {chosenStudent ? (
                    fetchingClassResult ? (
                      <Skeleton height="80px" />
                    ) : hasRoleOrHigher(Role.Editor) ? (
                      <Textarea
                        name="headRemark"
                        bg={colors.background.secondary}
                        value={editedSummaryEntry.headRemark || ""}
                        onChange={handleSummaryInputChange}
                        placeholder="Enter head teacher's remarks..."
                        minH="80px"
                      />
                    ) : (
                      <Text
                        fontSize="sm"
                        minH="80px"
                        p={3}
                        bg={colors.background.secondary}
                        borderRadius="md"
                      >
                        {editedSummaryEntry.headRemark || "No remarks"}
                      </Text>
                    )
                  ) : (
                    <Text color="gray.400">No student selected</Text>
                  )}
                </VStack>
              </Box>
            </VStack>
          </VStack>
        </>
      )}
    </>
  );
}
